import React from 'react';
import { motion } from 'framer-motion';

// Icons for features
const DataAnalyticsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H12V3H3V12H7V21H16V12H21Z"></path>
  </svg>
);

const AutomationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>
);

const RealTimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const SecurityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const IntegrationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
    <path d="M9 12h6"></path>
    <path d="M9 16h6"></path>
  </svg>
);

const PredictiveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);

const features = [
  {
    id: 1,
    title: "Advanced Data Analytics",
    description: "Our platform uses cutting-edge algorithms to analyze customer behavior and extract actionable insights from complex datasets.",
    icon: <DataAnalyticsIcon />,
  },
  {
    id: 2,
    title: "Workflow Automation",
    description: "Streamline your marketing processes with automated workflows that respond to customer actions and trigger personalized campaigns.",
    icon: <AutomationIcon />,
  },
  {
    id: 3,
    title: "Real-Time Monitoring",
    description: "Track campaign performance as it happens with real-time dashboards and alerts that keep your team informed and agile.",
    icon: <RealTimeIcon />,
  },
  {
    id: 4,
    title: "Enterprise-Grade Security",
    description: "Rest easy knowing your data is protected by industry-leading security protocols, including end-to-end encryption and access controls.",
    icon: <SecurityIcon />,
  },
  {
    id: 5,
    title: "Seamless Integrations",
    description: "Connect with your existing tech stack through our extensive API and pre-built integrations with popular marketing tools.",
    icon: <IntegrationIcon />,
  },
  {
    id: 6,
    title: "Predictive Modeling",
    description: "Leverage machine learning models that anticipate customer needs and help you stay ahead of market trends.",
    icon: <PredictiveIcon />,
  }
];

const FeatureCard = ({ feature, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{
        y: -10,
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
      className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 group border border-white/20 hover:border-white/40"
    >
      <motion.div
        className="flex items-center justify-center w-16 h-16 bg-white/20 rounded-xl mb-6 group-hover:bg-white/30 transition-colors duration-300"
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.6 }}
      >
        <div className="text-white">
          {feature.icon}
        </div>
      </motion.div>
      <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-yellow-200 transition-colors duration-300">
        {feature.title}
      </h3>
      <p className="text-gray-100 leading-relaxed group-hover:text-white transition-colors duration-300">
        {feature.description}
      </p>
      {/* <motion.div
        className="mt-6 flex items-center text-primary2 dark:text-blue-400 font-medium"
        whileHover={{ x: 5 }}
      >
        Learn more
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </motion.div> */}
    </motion.div>
  );
};

const FeaturesSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 via-red-600 to-purple-600 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Powerful Features for Data-Driven Teams
          </h2>
          <p className="text-xl text-gray-100">
            Unlock the full potential of your customer data with our comprehensive analytics platform
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>

        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary2 hover:bg-primary3 text-white font-medium py-3 px-8 rounded-full shadow-lg transition-all duration-300"
          >
            Explore All Features
          </motion.button>
        </motion.div> */}

        {/* Background decorations */}
        <motion.div
          className="hidden lg:block absolute -z-10 top-1/4 right-0 w-64 h-64 bg-white/10 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        ></motion.div>
        <motion.div
          className="hidden lg:block absolute -z-10 bottom-1/3 left-0 w-96 h-96 bg-white/10 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        ></motion.div>
        <motion.div
          className="hidden lg:block absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-white/5 rounded-full opacity-40 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
        ></motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
